/**
 * The whole vocabulary a recommendation may use to explain itself.
 *
 * A reason is ticked on a card and names an input that was requested, could be checked for that frame, and matched. A note is shown without a tick and never argues against a frame: it either says that other styles may also work, reports a catalog field that is not recorded, or shows a size outside the selected range. Nothing here describes stock — the stock group is decided and explained by the server. Codes and parameters only: every sentence belongs to the screen, in English or Arabic.
 */
import type { FrameShape, NamedFaceShape } from '@optiflow/types';
import type { Dimension } from './dimension';
import type { StylePrinciple } from './style-matrix';

export const ReasonCode = {
  FACE_SHAPE_COMPLEMENT: 'FACE_SHAPE_COMPLEMENT',
  FACE_SHAPE_COMPLEMENT_BOTH: 'FACE_SHAPE_COMPLEMENT_BOTH',
  FACE_SHAPE_COMPLEMENT_ONE: 'FACE_SHAPE_COMPLEMENT_ONE',
  DIMENSION_MATCH: 'DIMENSION_MATCH',
  MATCHES_PREFERRED_STYLE: 'MATCHES_PREFERRED_STYLE',
  PREFERRED_BRAND: 'PREFERRED_BRAND',
  PREFERRED_COLOUR: 'PREFERRED_COLOUR',
  WITHIN_BUDGET: 'WITHIN_BUDGET',
} as const;
export type ReasonCode = (typeof ReasonCode)[keyof typeof ReasonCode];

export const NoteCode = {
  OTHER_STYLES_MAY_WORK: 'OTHER_STYLES_MAY_WORK',
  FRAME_SHAPE_NOT_RECORDED: 'FRAME_SHAPE_NOT_RECORDED',
  DIMENSION_OUTSIDE_RANGE: 'DIMENSION_OUTSIDE_RANGE',
  DIMENSION_NOT_RECORDED: 'DIMENSION_NOT_RECORDED',
  BRAND_NOT_RECORDED: 'BRAND_NOT_RECORDED',
  COLOUR_NOT_RECORDED: 'COLOUR_NOT_RECORDED',
} as const;
export type NoteCode = (typeof NoteCode)[keyof typeof NoteCode];

/** One dimension compared with the selected range. */
export interface DimensionComparison {
  readonly dimension: Dimension;
  readonly valueMm: number;
  readonly minMm: number;
  readonly maxMm: number;
}

type NoParams = Readonly<Record<string, never>>;

export type Reason =
  | {
      readonly code: typeof ReasonCode.FACE_SHAPE_COMPLEMENT;
      readonly params: {
        readonly faceShape: NamedFaceShape;
        readonly frameShape: FrameShape;
        readonly principle: StylePrinciple;
      };
    }
  | {
      readonly code: typeof ReasonCode.FACE_SHAPE_COMPLEMENT_BOTH;
      readonly params: {
        readonly componentA: NamedFaceShape;
        readonly componentB: NamedFaceShape;
        readonly frameShape: FrameShape;
      };
    }
  | {
      readonly code: typeof ReasonCode.FACE_SHAPE_COMPLEMENT_ONE;
      readonly params: {
        readonly component: NamedFaceShape;
        readonly frameShape: FrameShape;
        readonly principle: StylePrinciple;
      };
    }
  | {
      readonly code: typeof ReasonCode.DIMENSION_MATCH;
      readonly params: { readonly dimensions: readonly DimensionComparison[] };
    }
  | {
      readonly code: typeof ReasonCode.MATCHES_PREFERRED_STYLE;
      readonly params: { readonly frameShape: FrameShape };
    }
  | {
      readonly code: typeof ReasonCode.PREFERRED_BRAND;
      readonly params: { readonly brandId: string };
    }
  | {
      readonly code: typeof ReasonCode.PREFERRED_COLOUR;
      readonly params: { readonly colourName: string };
    }
  | { readonly code: typeof ReasonCode.WITHIN_BUDGET; readonly params: NoParams };

export type Note =
  | { readonly code: typeof NoteCode.OTHER_STYLES_MAY_WORK; readonly params: NoParams }
  | { readonly code: typeof NoteCode.FRAME_SHAPE_NOT_RECORDED; readonly params: NoParams }
  | { readonly code: typeof NoteCode.DIMENSION_OUTSIDE_RANGE; readonly params: DimensionComparison }
  | {
      readonly code: typeof NoteCode.DIMENSION_NOT_RECORDED;
      readonly params: { readonly dimension: Dimension };
    }
  | { readonly code: typeof NoteCode.BRAND_NOT_RECORDED; readonly params: NoParams }
  | { readonly code: typeof NoteCode.COLOUR_NOT_RECORDED; readonly params: NoParams };
